import {
  ProductClass,
  TradeDirection,
  TradeFlow,
} from '../../graphQL/types/shared';
import {
  CountryMetadatumLevel as Level,
  DataSource as Source,
  failIfValidOrNonExhaustive,
  ICCYDatum,
  ILoadable,
  LoadableStatus
} from '../../Utils';
import {
  GraphSubject as Subject,
} from '../../viz/Utils';
import {
  IMetadatum,
} from '../fetchedData/countryMetadata';
import getSize from '../network/getSize';
import assignCoords from './assignCoords';
import assignSection from './assignSection';
import {
  getCountryAggregator,
  getRegionAndSubregionAggregator,
} from './countryAggregator';
import cumulativeSum from './cumulativeSum';
import filterAndAssignValue from './filterAndAssignValueCountry';
import defaultGetColor from './getCountryColor';
import getSectionGetter from './getCountrySectionGetter';
import getSubSectionGetter from './getCountrySubSectionGetter';
import getValueGetter from './getValueGetter';
import groupIntoRibbons from './groupIntoRibbons';
import {
  AggregatePerYearPerSubSection,
  IDeflatorsMap,
  WithValue,
} from './internalUtils';
import {
  IMergeOutput,
  InflationAdjustment,
  Layout,
  Ordering,
  PopulationAdjustment,
} from './Utils';

export interface IMergeInput {
  subject: Subject.Country;
  source: Source.CCY;

  mainData: ILoadable<ICCYDatum[]>;
  metadata: ILoadable<Map<number, IMetadatum>>;
  levelMapping: ILoadable<Map<number, number>>;
  deflatorsData: ILoadable<IDeflatorsMap>;

  productClass: ProductClass;
  tradeDirection: TradeDirection;
  tradeFlow: TradeFlow;
  level: Level;
  deselectedCategories: number[];
  svgWidth: number | undefined;
  svgHeight: number | undefined;
  ordering: Ordering;
  populationAdjustment: PopulationAdjustment;
  inflationAdjustment: InflationAdjustment;
  layout: Layout;
  startYear: number;
  endYear: number;

  // Used in testing:
  getColor?: (section: number) => string;
}

const merge = (input: IMergeInput): ILoadable<IMergeOutput> => {
  const {
    tradeFlow, tradeDirection,
    metadata, level, deselectedCategories,
    mainData, deflatorsData,
    inflationAdjustment, startYear, endYear,
    levelMapping, groupsMetadata, groupMembersMetadata,
    getColor = defaultGetColor,
    ordering, layout,
    svgWidth, svgHeight,
    subject,
  } = input;
  const getValue = getValueGetter(tradeFlow, tradeDirection);
  const withValue = filterAndAssignValue(
    mainData, deflatorsData,
    inflationAdjustment,
    startYear, endYear, getValue,
  );
  const getId = ({partner_id}: ICCYDatum) => partner_id;


  let getSection;
  let getSubSection;
  let assignedSection;
  let subregionToRegionMap;

  let regionsMetadataMap, subregionsMetadataMap, regionMembershipsMap, subregionMembershipsMap;
  if(groupsMetadata.status === LoadableStatus.Present && groupMembersMetadata.status === LoadableStatus.Present) {
    const groupsMetadataEntries = [...groupsMetadata.data.entries()];

    regionsMetadataMap = {
      status: LoadableStatus.Present,
      data: new Map(
        groupsMetadataEntries.filter(g => g[0] !== 6 && g[1].group_type === "region") // manually filtering out group-6, 'Other'
        .map(g => {

          const name_en = g[1].group_name;
          const name_short_en = g[1].group_name;
          const code = g[1].group_name;
          return [`group-${g[0]}`, {name_en, name_short_en, code}];
        })
      )
    };
  
    subregionsMetadataMap = {
      status: LoadableStatus.Present,
      data: new Map(
        groupsMetadataEntries.filter(g => g[1].group_type === "subregion")
        .map(g => {
          const name_en = g[1].group_name;
          const name_short_en = g[1].group_name;
          const code = g[1].group_name;
          return [`group-${g[0]}`, {name_en, name_short_en, code}]
        })
      )
    };
  
  
    const groupMembersMetadataEntries = [...groupMembersMetadata.data.entries()];
    regionMembershipsMap = {
      status: LoadableStatus.Present,
      data: new Map(
        groupMembersMetadataEntries.map(g => {
          const location_id = g[0];

          let regionMembershipEntries = g[1].filter(gr => gr.group_type === "region");
          if(regionMembershipEntries.length == 1) {
            return [g[0], `group-${regionMembershipEntries[0].group_id}`];
          }
        })
      )
    };
  
  
    subregionMembershipsMap = {
      status: LoadableStatus.Present,
      data: new Map(
        groupMembersMetadataEntries.map(g => {
          const location_id = g[0];
          let subregionMembershipEntries = g[1].filter(gr => gr.group_type === "subregion");
          if(subregionMembershipEntries.length == 1) {
            return [g[0], `group-${subregionMembershipEntries[0].group_id}`];
          }
        })
  
    )
    }

    subregionToRegionMap = new Map(
        groupsMetadataEntries.filter(g => g[1].group_type === "subregion")
        .map(g => {
          const parentId = g[1].parent_id;
          return [`group-${g[0]}`, `group-${parentId}`]
        })
      );
      
  } else {
    regionsMetadataMap = {status: LoadableStatus.NotPresent};
    subregionsMetadataMap = {status: LoadableStatus.NotPresent};
    regionMembershipsMap = {status: LoadableStatus.NotPresent};
    subregionMembershipsMap = {status: LoadableStatus.NotPresent};
    subregionToRegionMap = {status: LoadableStatus.NotPresent};
  }

  // const getSection = getSectionGetter(levelMapping, getId);
  // const getSubSection = getSubSectionGetter(levelMapping, metadata, level, getId);
  // const assignedSection = assignSection<ICCYDatum>(
  //   withValue, getSection, getSubSection, deselectedCategories, getColor,
  // );
  let aggregate: ILoadable<AggregatePerYearPerSubSection<ICCYDatum>>;
  // if (level === Level.region) {
  //   aggregate = getRegionAndSubregionAggregator<WithValue<ICCYDatum>>();
  // } else if (level === Level.country) {
  //   aggregate = getCountryAggregator<WithValue<ICCYDatum>>();
  // } else {
  //   failIfValidOrNonExhaustive(level, 'Invalid country level');
  //   // The following lines will never be executed:
  //   aggregate = undefined as any;
  // }

  if (level === Level.region) {
    // getSection = getSectionGetter(levelMapping, getId);
    // getSubSection = getSubSectionGetter(levelMapping, metadata, level, getId);
    getSection = getSectionGetter(regionMembershipsMap, getId);
    getSubSection = getSubSectionGetter(regionMembershipsMap, regionsMetadataMap, level, getId);
    assignedSection = assignSection<ICPYDatum>(
      withValue, getSection, getSubSection, deselectedCategories, getColor, subregionToRegionMap, level
    );
  
    aggregate = getRegionAndSubregionAggregator<WithValue<ICPYDatum>>();

  } else if(level === Level.subregion) {
    getSection = getSectionGetter(subregionMembershipsMap, getId);
    getSubSection = getSubSectionGetter(subregionMembershipsMap, subregionsMetadataMap, level, getId);
    assignedSection = assignSection<ICPYDatum>(
      withValue, getSection, getSubSection, deselectedCategories, getColor, subregionToRegionMap, level
    );
    aggregate = getRegionAndSubregionAggregator<WithValue<ICPYDatum>>();
  } else if (level === Level.country) {

    // getSection = getSectionGetter(levelMapping, getId);
    // getSubSection = getSubSectionGetter(levelMapping, metadata, level, getId);
    getSection = getSectionGetter(regionMembershipsMap, getId);
    getSubSection = getSubSectionGetter(regionMembershipsMap, metadata, level, getId);

    assignedSection = assignSection<ICPYDatum>(
      withValue, getSection, getSubSection, deselectedCategories, getColor, subregionToRegionMap, level
    );
  
    aggregate = getCountryAggregator<WithValue<ICPYDatum>>();

  } else {
    failIfValidOrNonExhaustive(level, 'Invalid country level');
    // The following lines will never be executed:
    aggregate = undefined as any;
  }

  
  const groupedIntoRibbons = groupIntoRibbons(
    assignedSection, aggregate, ordering, layout,
    inflationAdjustment, PopulationAdjustment.NotAdjusted, tradeDirection, tradeFlow,
  );

  const cumulativeSummed = cumulativeSum(groupedIntoRibbons);
  const size = getSize(svgWidth, svgHeight);
  const output = assignCoords(
    cumulativeSummed, size, layout, startYear, endYear, subject,
  );
  return output;
};

export default merge;
